/**
 * TOSS Web Application — one row of the side navigation
 *
 * The cabinet's row, now on both surfaces (owner, 26.08.2026). What it replaced on the internal
 * side: a 52 px row with a 24 px glyph whose active state was `bg-[#FAFAFA]` — a white one step
 * from the background, so the lit item was found by elimination rather than seen. Here the active
 * row is tinted and its label lifts to full contrast — the same signal the portal has used since Р2.
 *
 * Since 2026-08-27 the rail is dark, and the row's four colours moved to `--sidebar-*` tokens
 * (active fill and label, badge, focus ring). Everything else — the resting label, the hover, the
 * chevron — still reads the ordinary `--color-*` names, which the rail redefines within itself;
 * see the `[data-sidebar-surface='dark']` block in `index.css`. The four exceptions are exceptions
 * because they were written on `--color-primary-*`, and that scale cannot be redefined inside the
 * rail: the «Новая заявка» button standing right above these rows is built from the same steps.
 *
 * Three shapes, one component:
 *
 *  - **leaf** — a link;
 *  - **parent** — a button. It expands and does NOT navigate, even when the node carries a path:
 *    the internal `/directory` has no screen of its own, and a parent that sometimes navigated and
 *    sometimes did not would be a control you have to remember;
 *  - **nested** — a child row, inset and a shade quieter.
 *
 * `h-11 lg:h-10`: 44 px is the WCAG 2.5.5 target, and the one place this rail is touched by a
 * finger is the internal app's mobile drawer. Above 767 it is a mouse rail and 40 px is the
 * cabinet's number.
 */

import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Tooltip } from './Tooltip';

// ============================================================================
// Types
// ============================================================================

export interface SidebarNavRowProps {
  label: string;
  icon?: LucideIcon;
  /** Required for a leaf; ignored on a parent, which expands instead of navigating. */
  to?: string;
  isActive?: boolean;
  collapsed?: boolean;
  /** Unread count. Zero or absent draws nothing. */
  badgeCount?: number;
  /** A child row: inset, smaller glyph. */
  nested?: boolean;
  /** Renders the button form with a chevron. */
  expandable?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  /** Closes the mobile drawer after a leaf is followed. */
  onNavigate?: () => void;
  /** Suppress the collapsed-mode tooltip — a parent shows a flyout instead. */
  noTooltip?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const SidebarNavRow = forwardRef<HTMLElement, SidebarNavRowProps>(function SidebarNavRow(
  {
    label,
    icon: Icon,
    to,
    isActive = false,
    collapsed = false,
    badgeCount = 0,
    nested = false,
    expandable = false,
    isExpanded = false,
    onToggleExpand,
    onNavigate,
    noTooltip = false,
  },
  ref
) {
  const glyph = nested ? 'w-4 h-4' : 'w-[18px] h-[18px]';

  const classes = [
    'relative w-full flex items-center rounded-[var(--radius-md)]',
    'transition-colors duration-150',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sidebar-focus-ring)]',
    collapsed ? 'justify-center h-10 w-10 mx-auto' : `gap-3 h-11 lg:h-10 ${nested ? 'pl-9 pr-3' : 'px-3'}`,
    isActive
      ? 'bg-[var(--sidebar-row-active-bg)] text-[var(--sidebar-row-active-text)] font-medium'
      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]',
  ].join(' ');

  const body = (
    <>
      <span className="relative flex-shrink-0">
        {Icon && <Icon className={glyph} strokeWidth={isActive ? 2.2 : 1.9} />}
        {collapsed && badgeCount > 0 && (
          // No room for a number on a 40 px square, and a clipped one reads worse than none: the
          // dot says «something new here», the expanded row says how much.
          <span
            data-testid="sidebar-row-dot"
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--sidebar-badge-bg)]"
            aria-hidden="true"
          />
        )}
      </span>

      {!collapsed && (
        <>
          <span className="flex-1 min-w-0 truncate text-sm text-left">{label}</span>

          {badgeCount > 0 && (
            <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--sidebar-badge-bg)] text-[var(--sidebar-badge-text)] text-xs font-medium flex items-center justify-center">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}

          {expandable && (
            <ChevronDown
              className={`w-4 h-4 flex-shrink-0 text-[var(--color-text-disabled)] transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              aria-hidden="true"
            />
          )}
        </>
      )}
    </>
  );

  const row = expandable ? (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      onClick={onToggleExpand}
      aria-expanded={collapsed ? undefined : isExpanded}
      aria-label={collapsed ? label : undefined}
      className={classes}
    >
      {body}
    </button>
  ) : (
    <Link
      ref={ref as React.Ref<HTMLAnchorElement>}
      to={to ?? '#'}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
      className={classes}
    >
      {body}
    </Link>
  );

  if (!collapsed || noTooltip) return row;

  // Collapsed, the icon is the only label there is — the tooltip is what makes the row readable
  // rather than a guess.
  return (
    <Tooltip content={badgeCount > 0 ? `${label} · ${badgeCount}` : label} side="right">
      {row}
    </Tooltip>
  );
});

export default SidebarNavRow;
